import { combineReducers, configureStore } from "@reduxjs/toolkit";
import optionReducer from "./slices/optionSlice";
import tabsReducer from "./slices/tabsSlice";
import storage from "redux-persist/lib/storage/session";
import dashboardDataReducer from "./slices/dashboardDataSlice";
import authSlice from "./slices/authSlice";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import ccifDataReducer from "./slices/ccifDataSlice";
import selectCcifSlice from "./slices/selectCcifSlice";
import notificationSlice from "./slices/notificationSlice";
import snackbarSlice from "./slices/snackbarSlice";
import mobileEmailSlice from "./slices/mobileEmailSlice";
import accountSummaryDataSlice from "./slices/accountSummarySlice";
import { encryptTransform } from "redux-persist-transform-encrypt";
import statusPopupSlice from "./slices/statusPopupSlice";
import disbursementButtonSlice from "./slices/disbursementButtonSlice";
import documentFormSlice from "./slices/documentFormSlice";
import notificationToast from "./slices/toastNotificationSlice";
import paymentUpdateDetailsSlice from "./slices/paymentUpdateDetailsSlice";
import keyRegistrarReducer from "./slices/keyRegistrarSlice";
import stepperReducer from "./slices/stepperDynamicFormSlice";
import loadingReducer from "./slices/loadingSlice";
import applicationDetailsReducer from "./slices/applicationLoanDetailsSlice";
import chatSlice from "./slices/chatSlice";

// Separate persist config for auth slice
const authPersistConfig = {
  key: "auth",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: [
    "token",
    "authStatus",
    "userProfile",
    "permission",
    "businessType",
    "loanCategory",
    "requestSignature"
  ],
};

// Separate persist config for stepper slice
const stepperPersistConfig = {
  key: "stepper",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: [
    "auth",
    "activeVerticalIndex",
    "activeHorizontalIndex",
    "completedHorizontalIndex",
    "formData",
    "trackingId",
    "isMobileVerified",
    "isEmailVerified",
    "isBypassLogin",
    "aadhaarApplicationNo",
    "hasPan",
    "panNumber",
    "isPanVerified",
    "adharMandatory",
    "showAadhaar",
    "lastPanNumber",
    "lastVerifiedPhoneNumber",
    "lastVerifiedEmail",
    "isEligibilityCheckSubmitted",
    "isDocumentVerified",
    "notInList",
    "aadhaarVerifiedDetails",
    "coApplicantVerification",
    "currentCoApplicantIdx",
    "extraCourses",
    "entranceExamEntries",
    "cgpaModes",
    "extraCoApplicant",
    "breStatus",
    "breData",
    "fetchUserApplicationData",
    "isEligible",
    "consentApplicationNo",
    "aggregatorDocId",
    "documentApplicantType",  
    "digiLockerLoaderId",
    "partnerStartedJourney",
    "activeLayoutTab"
  ],
};

const tabsPersistConfig = {
  key: "tabs",
  storage,
  transforms: [
    encryptTransform({
      secretKey: "Sq@r3G@m3-$3cr3t-k3y",
      onError: function (error) {
        console.error(error, "redux encryption error");
      },
    }),
  ],
  whitelist: ["selectedIndex", "summaryPageActiveTab"],
};

const rootReducer: any = combineReducers({
  auth: persistReducer(authPersistConfig, authSlice),
  option: optionReducer,
  tabs: persistReducer(tabsPersistConfig, tabsReducer),
  dashboardData: dashboardDataReducer,
  snackbarSlice: snackbarSlice,
  notification: notificationSlice,
  accountSummaryData: accountSummaryDataSlice,
  statusPopup: statusPopupSlice,
  disbursementButtonSlice: disbursementButtonSlice,
  documentFormSlice: documentFormSlice,
  toastNotification: notificationToast,
  paymentUpdateDetailsSlice: paymentUpdateDetailsSlice,
  keyRegistrar: keyRegistrarReducer,
  stepper: persistReducer(stepperPersistConfig, stepperReducer),
  loading: loadingReducer,
  applicationLoanDetailsData: applicationDetailsReducer,
  chat: chatSlice,
});

export const store = configureStore({
  reducer: rootReducer,
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
